import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Send, MapPin, Share2, Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";

const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws");

function formatTime(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function LocationMessage({ metadata, isOwn }) {
  const { address, lat, lng } = metadata || {};
  const mapUrl = lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg ${isOwn ? "bg-white/15 border border-white/20" : "bg-teal/10 border border-teal/20"}`}>
      <MapPin size={16} className={`${isOwn ? "text-white/80" : "text-teal"} mt-0.5 shrink-0`} />
      <div>
        <p className={`text-sm font-medium ${isOwn ? "text-white" : ""}`}>{address || "Location shared"}</p>
        {mapUrl && (
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className={`text-xs ${isOwn ? "text-white/70 hover:text-white" : "text-teal hover:underline"}`}>
            Open in Maps
          </a>
        )}
      </div>
    </div>
  );
}

function MatchDetailsMessage({ metadata, isOwn }) {
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg ${isOwn ? "bg-white/15 border border-white/20" : "bg-coral/10 border border-coral/20"}`}>
      <Share2 size={16} className={`${isOwn ? "text-white/80" : "text-coral"} mt-0.5 shrink-0`} />
      <div>
        <p className={`text-sm font-medium ${isOwn ? "text-white" : ""}`}>{metadata?.club_name || "Match"}</p>
        <p className={`text-xs ${isOwn ? "text-white/70" : "text-muted-foreground"}`}>
          {metadata?.date} at {metadata?.time} &middot; {metadata?.mode}
        </p>
      </div>
    </div>
  );
}

function SystemMessage({ content }) {
  return (
    <div className="flex justify-center py-1">
      <span className="text-[11px] text-muted-foreground/70 bg-muted/30 px-3 py-0.5 rounded-full">{content}</span>
    </div>
  );
}

function ChatBubble({ msg, isOwn }) {
  if (msg.message_type === "system") return <SystemMessage content={msg.content} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex flex-col ${isOwn ? "items-end" : "items-start"} mb-2`}
    >
      {!isOwn && (
        <span className="text-[10px] text-muted-foreground/70 ml-1 mb-0.5">{msg.sender_name}</span>
      )}
      <div
        className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
          isOwn
            ? "bg-coral text-white rounded-br-sm"
            : "bg-muted/60 text-foreground rounded-bl-sm"
        }`}
      >
        {msg.message_type === "location" ? (
          <LocationMessage metadata={msg.metadata} isOwn={isOwn} />
        ) : msg.message_type === "match_details" ? (
          <MatchDetailsMessage metadata={msg.metadata} isOwn={isOwn} />
        ) : (
          <p className="text-sm leading-relaxed break-words">{msg.content}</p>
        )}
      </div>
      <span className={`text-[10px] text-muted-foreground/50 mt-0.5 ${isOwn ? "mr-1" : "ml-1"}`}>
        {formatTime(msg.created_at)}
      </span>
    </motion.div>
  );
}

export default function MatchChat({ matchId, match }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  // Load message history
  useEffect(() => {
    api.get(`/matches/${matchId}/messages?limit=100`)
      .then(r => {
        setMessages(r.data);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {});
  }, [matchId, scrollToBottom]);

  // WebSocket connection with polling fallback
  useEffect(() => {
    if (!user || !matchId) return;
    const token = localStorage.getItem("padel_access_token");
    if (!token) return;

    let pollInterval = null;
    const lastSeenRef = { current: "" };

    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(async () => {
        try {
          const r = await api.get(`/matches/${matchId}/messages?limit=50`);
          const newMsgs = r.data;
          if (newMsgs.length > 0) {
            const lastId = newMsgs[newMsgs.length - 1]._id;
            if (lastId !== lastSeenRef.current) {
              lastSeenRef.current = lastId;
              setMessages(newMsgs);
              setTimeout(scrollToBottom, 50);
            }
          }
        } catch (err) { console.warn("Chat poll failed", err); }
      }, 3000);
    };

    const wsUrl = `${WS_BASE}/api/ws/chat/${matchId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    };
    ws.onclose = () => { setConnected(false); startPolling(); };
    ws.onerror = () => { setConnected(false); startPolling(); };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.some(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        setTimeout(scrollToBottom, 50);
      } catch (err) { console.warn("Chat message parse failed", err); }
    };

    // Start polling as fallback after a short delay if WS doesn't connect
    const fallbackTimer = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) startPolling();
    }, 2000);

    return () => {
      clearTimeout(fallbackTimer);
      if (pollInterval) clearInterval(pollInterval);
      ws.close();
      setConnected(false);
    };
  }, [matchId, user, scrollToBottom]);

  const sendMessage = (content, messageType = "text", metadata = {}) => {
    if (!content.trim()) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ content, message_type: messageType, metadata }));
    } else {
      // Fallback to REST
      api.post(`/matches/${matchId}/messages`, { content, message_type: messageType, metadata })
        .then(r => {
          setMessages(prev => [...prev, r.data]);
          setTimeout(scrollToBottom, 50);
        })
        .catch(() => {});
    }

    if (messageType === "text") setInput("");
    setShowShareMenu(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const shareMatchDetails = () => {
    if (!match) return;
    sendMessage(
      `Match at ${match.club_name}`,
      "match_details",
      {
        club_name: match.club_name,
        date: match.date,
        time: match.time,
        mode: match.mode,
        players: match.players?.length || 0,
        max_players: match.max_players,
      }
    );
  };

  const shareLocation = () => {
    if (!navigator.geolocation) {
      sendMessage("Location sharing not available", "text");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        sendMessage(
          "My current location",
          "location",
          { lat: pos.coords.latitude, lng: pos.coords.longitude, address: "Current Location" }
        );
      },
      () => {
        sendMessage("Could not get location", "text");
      }
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        Sign in to join the chat
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[400px] bg-card rounded-xl border border-border/50 overflow-hidden" data-testid="match-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 bg-muted/20">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-amber-400 animate-pulse"}`} />
          <span className="text-sm font-medium">Match Chat</span>
          {!connected && <span className="text-[9px] text-muted-foreground/60 bg-muted/30 px-1.5 py-0.5 rounded">polling</span>}
        </div>
        <span className="text-[10px] text-muted-foreground">
          {match?.players?.length || 0} players
        </span>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-muted-foreground/50 text-sm">
            No messages yet. Say hello!
          </div>
        )}
        <AnimatePresence>
          {messages.map((msg, i) => (
            <ChatBubble key={msg._id || i} msg={msg} isOwn={msg.sender_id === user?.id} />
          ))}
        </AnimatePresence>
      </div>

      {/* Share menu */}
      <AnimatePresence>
        {showShareMenu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border/30 bg-muted/10 px-3 py-2 flex gap-2 overflow-hidden"
          >
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs gap-1.5 h-8"
              onClick={shareMatchDetails}
              data-testid="share-match-details-btn"
            >
              <Share2 size={12} /> Match Details
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs gap-1.5 h-8"
              onClick={shareLocation}
              data-testid="share-location-btn"
            >
              <MapPin size={12} /> My Location
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 p-2.5 border-t border-border/50 bg-muted/10">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-full shrink-0"
          onClick={() => setShowShareMenu(!showShareMenu)}
          data-testid="chat-share-toggle"
        >
          <Smile size={18} className={showShareMenu ? "text-coral" : "text-muted-foreground"} />
        </Button>
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          className="rounded-full h-9 text-sm bg-background/50"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          size="icon"
          className="h-8 w-8 rounded-full bg-coral hover:bg-coral-600 text-white shrink-0"
          disabled={!input.trim()}
          data-testid="chat-send-btn"
        >
          <Send size={14} />
        </Button>
      </form>
    </div>
  );
}
